import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldCategory,
  type FieldValues,
  type OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  type ComputedSection,
  getComputedOutcomeLabels,
  isComputedSection,
} from 'features/libra/sections/computedSection/computedSectionTypes'
import { FormLayoutItemSchema } from 'features/libra/sections/formLayout'
import { ZonesSectionSchema } from 'features/libra/sections/zonesSection/zonesSectionTypes'
import { evaluateComputedSectionWithOverride } from 'features/libra/stepComponents/conditionalForm/computed/evaluateComputedSection'
import { getVisibleSectionIds } from 'features/libra/stepComponents/conditionalForm/conditionalFormSection/conditionalFormVisibility'
import { resolveCloseoutAttribution } from 'features/libra/stepComponents/niiReport/closeoutAttribution'
import { niiReportConfigSchema } from 'features/libra/stepComponents/niiReport/niiReportApi'
import {
  parseCofFromFieldValues,
  parseConfidenceFromFieldValues,
  parseZonesFromFieldValues,
  ZONE_FIELD_IDS,
  zoneAnalysisConfigSchema,
  type ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import {
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import { getInactiveStepIds } from 'features/libra/workflow/inactiveSteps'
import type { LibraWorkflowStep } from 'features/libra/workflow/workflowStepsApi'
import { z } from 'zod'

import { buildNiiArrayTableBlocks } from './buildNiiArrayTableBlocks'
import {
  buildNiiFormSectionBlocks,
  parseFormSection,
} from './buildNiiFormSectionBlocks'
import {
  buildNiiReviewSection,
  formatNiiSignoffTimestamp,
} from './buildNiiReviewContent'
import { buildNiiScreeningContent } from './buildNiiScreeningContent'
import {
  buildDamageMechanismsTable,
  buildDesignAndFunctionTable,
  buildNiiZoneAnalysisContent,
  resolveZoneColor,
} from './buildNiiZoneAnalysisContent'
import {
  formatOverriddenBy,
  type NiiOverrideDetail,
  type NiiQuestionList,
  type NiiReportContentInputs,
  type NiiReportSection,
  type NiiZoneInventoryEntry,
  resolveNiiReportSections,
} from './niiReportContentTypes'

type StepLike = Pick<
  LibraWorkflowStep,
  'id' | 'name' | 'component' | 'config' | 'branch'
>

type NiiSectionBuilder = (
  step: StepLike,
  ctx: NiiReportContentInputs
) => NiiReportSection | undefined

function buildZoneInventory(
  zones: ReadonlyArray<ZoneParams>
): ReadonlyArray<NiiZoneInventoryEntry> {
  return zones.map((zone, zoneIndex) => ({
    title: zone.title || zone.description || `Zone ${zoneIndex + 1}`,
    color: resolveZoneColor(zone, zoneIndex),
    zoneIndex,
    description: zone.description || undefined,
    designAndFunction: buildDesignAndFunctionTable(zone),
    damageMechanisms: buildDamageMechanismsTable(zone),
  }))
}

function getStepSections(step: StepLike): unknown[] {
  const sections = step.config.sections
  return Array.isArray(sections) ? sections : []
}

const FormLayoutSchema = z.array(FormLayoutItemSchema)
const SectionIdSchema = z.object({ id: z.string() })

/**
 * Sections the ConditionalForm shows for these field values. Hidden sections
 * keep their last values, so reading them would report stale answers. A step
 * without a form layout reports every section.
 */
function getVisibleStepSections(
  step: StepLike,
  fieldValues: FieldValues
): unknown[] {
  const sections = getStepSections(step)
  const layout = FormLayoutSchema.safeParse(step.config.form)
  if (!layout.success) return sections

  const visibleIds = new Set(getVisibleSectionIds(layout.data, fieldValues))
  return sections.filter((section) => {
    const parsed = SectionIdSchema.safeParse(section)
    return parsed.success && visibleIds.has(parsed.data.id)
  })
}

/**
 * Zones for a step's embedded zones section, read from the same
 * `zoneFieldName` the in-app ZonesSectionView uses (empty means empty — no
 * substituting zones from another field). Undefined when `sections` has no
 * zones section.
 */
function resolveZonesForStep(
  sections: ReadonlyArray<unknown>,
  ctx: NiiReportContentInputs
): ReadonlyArray<ZoneParams> | undefined {
  for (const section of sections) {
    const parsed = ZonesSectionSchema.safeParse(section)
    if (parsed.success) {
      return parseZonesFromFieldValues(
        ctx.fieldValues,
        parsed.data.zoneFieldName
      ).zones
    }
  }
  return undefined
}

function buildOutcomeOverride(
  section: ComputedSection,
  fieldValues: FieldValues,
  ontologyFields: Record<string, OntologyField>
): NiiOverrideDetail | undefined {
  const evaluation = evaluateComputedSectionWithOverride(section, fieldValues, {
    ontologyFields,
  })
  if (evaluation.overrideValue === undefined) return undefined

  const { trueLabel, falseLabel } = getComputedOutcomeLabels(section)
  const label = (result: boolean) => (result ? trueLabel : falseLabel)
  return {
    label: section.title ?? 'Outcome',
    calculated:
      evaluation.computedResult === undefined
        ? undefined
        : label(evaluation.computedResult),
    value: label(evaluation.overrideValue),
    justification: evaluation.overrideJustification || undefined,
    by: evaluation.overrideActor,
    at:
      evaluation.overrideTimestamp &&
      formatNiiSignoffTimestamp(evaluation.overrideTimestamp),
  }
}

/** Label/value rows for an outcome override, for the PDF. */
export function buildOutcomeOverrideRows(
  override: NiiOverrideDetail
): ReadonlyArray<{ label: string; value: string }> {
  const overriddenBy = formatOverriddenBy(override)
  return [
    {
      label: `${override.label} override`,
      value: override.calculated
        ? `${override.value} (calculated: ${override.calculated})`
        : override.value,
    },
    ...(override.justification
      ? [{ label: 'Override reason', value: override.justification }]
      : []),
    ...(overriddenBy ? [{ label: 'Overridden by', value: overriddenBy }] : []),
  ]
}

function buildQuestionListsFromSections(
  sections: ReadonlyArray<unknown>,
  ontologyFields: Record<string, OntologyField>,
  fieldValues: FieldValues
): ReadonlyArray<NiiQuestionList> {
  const lists: NiiQuestionList[] = []
  // Field ids per emitted list, so a computed section's override can be
  // attached to the questionnaire it actually evaluates.
  const listFieldIds: Array<ReadonlySet<string>> = []

  for (const section of sections) {
    const formSection = parseFormSection(section)
    if (formSection && formSection.presentation?.variant === 'question-list') {
      const answers = formSection.fields.flatMap((fieldId) => {
        const field = ontologyFields[fieldId]
        if (field && field.category !== FieldCategory.INPUT) return []
        if (field && field.type !== 'boolean') return []
        return [
          {
            question: field?.name ?? fieldId,
            answer: unwrapFieldValue<boolean>(fieldValues[fieldId]) === true,
          },
        ]
      })
      if (answers.length === 0) continue
      lists.push({ title: formSection.title, answers })
      listFieldIds.push(new Set(formSection.fields))
      continue
    }

    if (isComputedSection(section)) {
      const override = buildOutcomeOverride(
        section,
        fieldValues,
        ontologyFields
      )
      if (!override) continue

      const parameterFields = (section.functionSpec?.parameters ?? []).map(
        (parameter) => parameter.field
      )
      const ownerIndex = listFieldIds.findLastIndex((fieldIds) =>
        parameterFields.some((fieldId) => fieldIds.has(fieldId))
      )
      if (ownerIndex >= 0) {
        lists[ownerIndex] = { ...lists[ownerIndex], override }
      } else {
        lists.push({
          title: section.title ?? section.id,
          answers: [],
          override,
        })
        listFieldIds.push(new Set())
      }
    }
  }

  return lists
}

/**
 * Generic ConditionalForm → PDF. Handles Pre-Screening, Data Gathering, and
 * any other ConditionalForm purely from section config.
 * Never returns undefined — workflow steps must always appear in the PDF.
 */
const buildConditionalFormPdfStep: NiiSectionBuilder = (step, ctx) => {
  const sections = getVisibleStepSections(step, ctx.fieldValues)

  const formBlocks = buildNiiFormSectionBlocks(
    sections,
    ctx.ontologyFields,
    ctx.fieldValues
  )

  const arrayTables = buildNiiArrayTableBlocks(
    sections,
    ctx.ontologyFields,
    ctx.fieldValues
  )

  const questionLists = buildQuestionListsFromSections(
    sections,
    ctx.ontologyFields,
    ctx.fieldValues
  )

  const stepZones = resolveZonesForStep(sections, ctx)
  const zoneInventory = stepZones ? buildZoneInventory(stepZones) : undefined

  return {
    stepId: step.id,
    title: step.name,
    formBlocks: formBlocks.length > 0 ? formBlocks : undefined,
    arrayTables: arrayTables.length > 0 ? arrayTables : undefined,
    questionLists: questionLists.length > 0 ? questionLists : undefined,
    zoneInventory,
  }
}

const buildNiiScreeningPdfStep: NiiSectionBuilder = (step, ctx) => {
  const data = buildNiiScreeningContent(
    step,
    ctx.ontologyFields,
    ctx.fieldValues
  )
  return {
    stepId: step.id,
    title: step.name,
    formBlocks: data?.sections,
  }
}

/**
 * Match NiiReview: ZoneAnalysis readonly always uses ZoneAnalysisReviewPanel
 * (overview / per-zone details), regardless of visibleTabs. CoF is reported
 * by the Data Gathering step that edits it; here it only scales coverage.
 * Zones and confidence come from this step's own config, exactly like
 * ZoneAnalysisDataProvider resolves them for the review UI.
 */
const buildZoneAnalysisPdfStep: NiiSectionBuilder = (step, ctx) => {
  const parsed = zoneAnalysisConfigSchema.safeParse(step.config)
  const zones = parsed.success
    ? parseZonesFromFieldValues(ctx.fieldValues, parsed.data.zoneFieldName)
        .zones
    : []
  const confidence = parseConfidenceFromFieldValues(
    ctx.fieldValues,
    parsed.success ? parsed.data.confidenceFieldName : undefined
  )

  const zoneBreakdownJustification =
    unwrapFieldValue<string>(
      ctx.fieldValues[ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]
    ) || undefined

  // Zone analysis was never performed: emit just the titled card.
  if (zones.length === 0 && !zoneBreakdownJustification) {
    return { stepId: step.id, title: step.name }
  }

  const zoneAnalysis = buildNiiZoneAnalysisContent(
    zones,
    parseCofFromFieldValues(ctx.fieldValues).cof,
    confidence,
    zoneBreakdownJustification
  )
  return {
    stepId: step.id,
    title: step.name,
    zoneAnalysis,
    zoneImages: ctx.zoneImages.length > 0 ? ctx.zoneImages : undefined,
  }
}

const buildNiiReportPdfStep: NiiSectionBuilder = (step, ctx) => {
  const parsed = niiReportConfigSchema.safeParse(step.config)
  const fields = parsed.success ? parsed.data.fields : ctx.reportFields

  const eligibility =
    unwrapFieldValue<EligibilityStatus>(ctx.fieldValues[fields.eligibility]) ??
    ELIGIBILITY_STATUS.UNCLEAR

  const recommendations =
    unwrapFieldValue<ReadonlyArray<string>>(
      ctx.fieldValues[fields.recommendations]
    ) ?? []

  const { justification, screenedOutAtPreScreening } =
    resolveCloseoutAttribution(
      ctx.fieldValues,
      fields,
      parsed.success ? parsed.data.preScreening : undefined
    )

  // Color by position in the FULL zone array (before filtering) so the dot
  // matches the same zone's color in the inventory, details, and reference
  // images elsewhere in the document.
  const zoneRecommendations = ctx.zones.flatMap((zone, index) => {
    if (!zone.zone_recommendations || zone.zone_recommendations.length === 0) {
      return []
    }
    return [
      {
        title: zone.title,
        color: resolveZoneColor(zone, index),
        zone_recommendations: zone.zone_recommendations,
      },
    ]
  })

  return {
    stepId: step.id,
    title: step.name,
    reportCloseout: {
      executiveSummary: unwrapFieldValue<string>(
        ctx.fieldValues[fields.executiveSummary]
      ),
      internalComments: unwrapFieldValue<string>(
        ctx.fieldValues[fields.internalComments]
      ),
      eligibility,
      recommendations,
      justification,
      screenedOutAtPreScreening,
      zoneRecommendations,
    },
  }
}

/**
 * Component → PDF builder registry. Mirrors NiiReview's review panel registry.
 */
export const NII_SECTION_BUILDERS: Record<string, NiiSectionBuilder> = {
  ConditionalForm: buildConditionalFormPdfStep,
  NiiScreening: buildNiiScreeningPdfStep,
  ZoneAnalysis: buildZoneAnalysisPdfStep,
  NiiReport: buildNiiReportPdfStep,
}

/**
 * Build PDF sections from the workflow. Every PDF-capable step is exported
 * in workflow order, so all download entry points (NiiReview, NiiReport,
 * PreScreening closeout) produce the same document and the section order
 * matches the workflow the user actually walked through.
 */
export function buildNiiReportContent(
  workflowSteps: ReadonlyArray<StepLike>,
  ctx: NiiReportContentInputs
): ReadonlyArray<NiiReportSection> {
  // Steps the ticket's branch decisions skipped hold no meaningful content.
  const inactiveStepIds = getInactiveStepIds(
    { steps: workflowSteps },
    ctx.fieldValues
  )
  const exportSteps = resolveNiiReportSections(
    workflowSteps.filter((step) => !inactiveStepIds.has(step.id)),
    new Set(Object.keys(NII_SECTION_BUILDERS))
  )

  const sections = exportSteps.map((step) => {
    const builder = NII_SECTION_BUILDERS[step.component]
    const built = builder?.(step, ctx)
    // Never drop a workflow step — unknown/empty builders still emit a titled card.
    return (
      built ?? {
        stepId: step.id,
        title: step.name,
      }
    )
  })

  // Zone reference images normally render inside the ZoneAnalysis section.
  // When the workflow has none, keep them visible by attaching them to the
  // last section that shows zones, or as their own section as a last resort.
  if (
    ctx.zoneImages.length > 0 &&
    !sections.some((section) => section.zoneImages?.length)
  ) {
    const targetIndex = sections.findLastIndex(
      (section) => section.zoneInventory && section.zoneInventory.length > 0
    )
    if (targetIndex >= 0) {
      sections[targetIndex] = {
        ...sections[targetIndex],
        zoneImages: ctx.zoneImages,
      }
    } else {
      sections.push({
        stepId: 'zone-reference-images',
        title: 'Zone Reference Images',
        zoneImages: ctx.zoneImages,
      })
    }
  }

  // Last, after any fallback image section: review & approval closes the
  // document.
  const reviewSection = buildNiiReviewSection(
    ctx.signoffConfig,
    ctx.signoffs,
    ctx.fieldValues,
    ctx.lifecycle
  )
  if (reviewSection) sections.push(reviewSection)

  return sections
}
